import React, { useState, useEffect } from "react";
import { reduxForm, Field, InjectedFormProps } from "redux-form";
import { useSelector, useDispatch } from "react-redux";
import { PAGES } from "../../../constants/localization";
import {
  ACTION_DISPLAY_TYPES,
} from "../../../constants/enums";
import EnrollmentApi from "../../../api/enrollment";
import PagePathConstants from "../../../constants/page-numbers";
import { SetEnrollmentData, UpdateCurrentPage } from "../../../actions/enrollment";
import GenericForm from "../../../components/Common/DynamicFields/GenericForm";
import PageBlock from "../../../components/Layouts/PageBlock";
import InputWrapper from "../../../containers/Pin/InputWrapper";
import Label from "../../../components/Common/Label";
import BooleanField from "../../../components/Common/DynamicFields/BooleanField";
import ActionRow from "../../../components/Layouts/ActionRow";
import Action from "../../../components/Common/Action";
import { trackEvent } from "../../../services/eventTracker";
import { EVENTS } from "../../../constants/events";
import i18n from "../../../i18n/config";
import EnrollmentFlowScreen from "../../Layouts/EnrollmentFlowScreen";
import { RootState } from "../../../types/redux";
import { ErrorObjV2 } from "../../../types/errors";
import { CommunicationsPreferencesType } from "../../../types/api/enrollment";
import DynamicModal from "../../Common/DynamicModal";
import DynamicErrorModalContent from "../../Common/DynamicErrorModalContent";

const GF = GenericForm as typeof GenericForm & {
  Body: React.ComponentType<{ children?: React.ReactNode }>;
};

type CustomProps = {
  setShowSpinner: (show: boolean) => void;
}

const CommunicationPreferencesForm: React.FC<CustomProps & InjectedFormProps<{}, CustomProps>> = ({ setShowSpinner }) => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<ErrorObjV2>({ errorTitle: "", errorMessage: "", errorCode: "", errorDescription: "", erroryType: 0, fieldId: "" });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [data, setData] = useState([]);
  const formData = useSelector(
    (state: RootState) => state.form[PAGES.ENROLLMENT.COMMUNICATION_PREFERENCES.FORM_NAME]
  );
  const userData = useSelector((state: RootState) => state.enrollment.userData);
  const currentPage = useSelector((state: RootState) => state.enrollment.currentPage);

  /**
   * Get communication preferences docs when userData is updated
   */
  useEffect(() => {
    if (userData.participantId) {
      loadSiteDocsFromServer();
    }
  }, [userData]);

  /**
   * Get communication preferences docs
   */
  const loadSiteDocsFromServer = async () => {
    try {
      const response = await EnrollmentApi.GetCommunicationPreferences(
        userData.participantId
      );

      setData(response.preferences);
    } catch (err) {
      setShowErrorModal(true);
      setErrors({
        ...errors,
        errorTitle: i18n.t("COMMON.SOMETHING_WENT_WRONG"),
        errorMessage: i18n.t("COMMON.PLEASE_TRY_AGAIN"),
      });
    }
  };

  /**
   * Form submission handling
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowSpinner(true);

    const { values } = formData;
    const checkedPreferences: string[] = [];

    Object.keys(values).forEach(key => {
      if (values[key]) {
        checkedPreferences.push(key);
      }
    });

    try {
      const response = await EnrollmentApi.CompleteWorkflow(
        userData.workflowId,
        checkedPreferences,
        userData.productGroupId,
        userData.clientId,
        userData.participantId
      );

      trackEvent(EVENTS.ENROLLMENT.COMMUNICATIONS_SUBMITTED, { success: true });
      dispatch(SetEnrollmentData({
        username: response.username,
        nextPage: PagePathConstants.COMPLETE,
      }))
      dispatch(UpdateCurrentPage(PagePathConstants.COMPLETE));
    } catch (err: any) {
      setShowSpinner(false);
      trackEvent(EVENTS.ENROLLMENT.COMMUNICATIONS_SUBMITTED, { success: false, failure_reason: err[0].errorMessage || PAGES.ENROLLMENT.ERROR_RETRY });
      setShowErrorModal(true);
      setErrors({
        ...errors,
        errorTitle: err[0]?.errorTitle || i18n.t("COMMON.SOMETHING_WENT_WRONG"),
        errorMessage: err[0]?.errorMessage || i18n.t("COMMON.PLEASE_TRY_AGAIN"),
        errorCode: err[0]?.errorCode,
      });
    }
  };

  const docElements = data.map((doc: CommunicationsPreferencesType) => {
    return (
      <InputWrapper key={doc.id}>
        <Label label={doc.label} required={true} hideLabel={false} />
        <Field
          id={doc.id}
          name={doc.id}
          component={BooleanField}
          checked={false}
          label={`${doc.text} (Check to subscribe)`}
          hideOptional={true}
        />
      </InputWrapper>
    );
  });

  return (
    <EnrollmentFlowScreen
      flowTitle="Communications Preferences"
      showBackButton={true}
      customBackButtonFunc={() => dispatch(UpdateCurrentPage(currentPage - 2))}
    >
      <GenericForm
        form={PAGES.ENROLLMENT.COMMUNICATION_PREFERENCES.FORM_NAME}
        onSubmit={handleSubmit}
      >
        <GF.Body>
          <PageBlock.Body>
            {docElements}
            <Action
              title={i18n.t("COMMON.FINISH")}
              displayType={ACTION_DISPLAY_TYPES.PRIMARY}
              type="submit"
              disabled={false}
            />
          </PageBlock.Body>
        </GF.Body>
      </GenericForm>

      <DynamicModal
        className="max-w-350 v2 v3"
        open={showErrorModal}
        setClose={() => setShowErrorModal(false)}
        showCloseIcon={false}
        header={null}
        body={<DynamicErrorModalContent title={errors?.errorTitle ?? ""} message={errors?.errorMessage ?? ""} errorCode={errors?.errorCode} />}
        footer={<Action title={i18n.t("COMMON.CLOSE")} onClick={() => setShowErrorModal(false)} displayType={ACTION_DISPLAY_TYPES.TEXT} className="btn-narrow" />}
      />
    </EnrollmentFlowScreen>
  );
};

CommunicationPreferencesForm.displayName =
  "CommunicationPreferencesFormComponent";

export default reduxForm<{}, CustomProps>({
  form: PAGES.ENROLLMENT.COMMUNICATION_PREFERENCES.FORM_NAME,
})(CommunicationPreferencesForm);
